import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Relatorios.module.css';
import { listar, COLECOES } from '../../services/firestore';
import {
  calcularStatusEstoque,
  posicaoDeveAparecer,
  STATUS,
} from '../../utils/estoque';
import { exportarPDF, exportarExcel } from '../../utils/exportar';

// ============================================================
// RELATÓRIO: ABAIXO DO MÍNIMO
// ============================================================
// Lista posições CRITICO ou ZERADO (qtd < min). Ordenado pela
// "deficiência" — quanto falta para chegar no mínimo — para
// priorizar a reposição.
// ============================================================

export default function RelMinimo() {
  const navigate = useNavigate();

  const [produtos, setProdutos] = useState([]);
  const [posicoes, setPosicoes] = useState([]);
  const [estoques, setEstoques] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtroEstoque, setFiltroEstoque] = useState('');
  const [incluirAtencao, setIncluirAtencao] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [prods, pos, ests] = await Promise.all([
        listar(COLECOES.PRODUTOS),
        listar(COLECOES.ESTOQUE_POSICAO),
        listar(COLECOES.ESTOQUES, { ordenarPor: 'nome' }),
      ]);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setPosicoes(pos);
      setEstoques(ests.filter((e) => e.ativo !== false));
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const linhas = useMemo(() => {
    const mapProdutos = {};
    produtos.forEach((p) => {
      mapProdutos[p.id] = p;
    });

    const r = [];
    posicoes.forEach((pos) => {
      const prod = mapProdutos[pos.produtoId];
      if (!prod) return;

      const estoqueDoc = estoques.find((e) => e.id === pos.estoqueId);
      const nivel = (estoqueDoc?.niveis || []).find(
        (n) => n.produtoId === pos.produtoId
      );
      const min = Number(nivel?.minimo) || 0;
      const max = Number(nivel?.maximo) || 0;
      const qtd = Number(pos.qtdReal) || 0;

      if (min <= 0) return;
      if (!posicaoDeveAparecer(qtd, min)) return;

      const statusKey = calcularStatusEstoque(qtd, min, max);

      const aceitos = incluirAtencao
        ? [STATUS.CRITICO, STATUS.ZERADO, STATUS.ATENCAO]
        : [STATUS.CRITICO, STATUS.ZERADO];

      if (!aceitos.includes(statusKey)) return;

      const repor = Math.max(min - qtd, 0);

      r.push({
        cor: prod.cor || '',
        resina: prod.resina || '',
        estoque: pos.estoqueNome || estoqueDoc?.nome || '(sem nome)',
        estoqueId: pos.estoqueId,
        qtd,
        minimo: min,
        maximo: max,
        repor,
        statusKey,
        statusLabel:
          statusKey === STATUS.ZERADO
            ? 'Zerado'
            : statusKey === STATUS.CRITICO
            ? 'Abaixo do mín'
            : 'Em atenção',
      });
    });

    let final = filtroEstoque ? r.filter((x) => x.estoqueId === filtroEstoque) : r;
    return final.sort((a, b) => b.repor - a.repor);
  }, [produtos, posicoes, estoques, incluirAtencao, filtroEstoque]);

  const resumo = useMemo(() => {
    const zerados = linhas.filter((l) => l.statusKey === STATUS.ZERADO).length;
    const criticos = linhas.filter((l) => l.statusKey === STATUS.CRITICO).length;
    const atencao = linhas.filter((l) => l.statusKey === STATUS.ATENCAO).length;
    return { total: linhas.length, zerados, criticos, atencao };
  }, [linhas]);

  const colunasExport = [
    { header: 'Cor', dataKey: 'cor' },
    { header: 'Resina', dataKey: 'resina' },
    { header: 'Estoque', dataKey: 'estoque' },
    { header: 'Qtd (L)', dataKey: 'qtd' },
    { header: 'Mín', dataKey: 'minimo' },
    { header: 'Repor', dataKey: 'repor' },
    { header: 'Status', dataKey: 'statusLabel' },
  ];

  function handlePDF() {
    const sub = [
      filtroEstoque
        ? `Estoque: ${estoques.find((e) => e.id === filtroEstoque)?.nome}`
        : '',
      incluirAtencao ? 'Incluindo "em atenção"' : '',
    ].filter(Boolean).join(' · ');
    exportarPDF({
      titulo: 'Produtos Abaixo do Mínimo',
      subtitulo: sub,
      colunas: colunasExport,
      dados: linhas,
      orientacao: 'landscape',
    });
  }

  function handleExcel() {
    exportarExcel({
      titulo: 'Produtos Abaixo do Mínimo',
      colunas: colunasExport,
      dados: linhas,
      nomeAba: 'Reposição',
    });
  }

  return (
    <>
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Abaixo do Mínimo</div>
          <div className={styles.pageSub}>
            Lista de reposição: posições com quantidade abaixo do mínimo cadastrado.
          </div>
        </div>
        <button
          className={styles.btnVoltar}
          onClick={() => navigate('/relatorios')}
        >
          ← Voltar para Relatórios
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '14px 0',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.resumo} style={{ marginTop: 14 }}>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Total de itens</div>
          <div className={styles.resumoValor}>{resumo.total}</div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Críticos</div>
          <div className={`${styles.resumoValor} ${styles.warn}`}>
            {resumo.criticos}
          </div>
        </div>
        <div className={styles.resumoCard}>
          <div className={styles.resumoLabel}>Zerados</div>
          <div className={`${styles.resumoValor} ${styles.warn}`}>
            {resumo.zerados}
          </div>
        </div>
        {incluirAtencao && (
          <div className={styles.resumoCard}>
            <div className={styles.resumoLabel}>Em atenção</div>
            <div className={`${styles.resumoValor} ${styles.info}`}>
              {resumo.atencao}
            </div>
          </div>
        )}
      </div>

      <div className={styles.filtros}>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Estoque</label>
          <select
            className={styles.filtroSelect}
            value={filtroEstoque}
            onChange={(e) => setFiltroEstoque(e.target.value)}
          >
            <option value="">Todos</option>
            {estoques.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.filtroField}>
          <label className={styles.filtroLabel}>Mostrar também</label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={incluirAtencao}
              onChange={(e) => setIncluirAtencao(e.target.checked)}
            />
            Itens &quot;em atenção&quot;
          </label>
        </div>

        <div className={styles.btnExportar}>
          <button
            className={styles.btnExp}
            onClick={handlePDF}
            disabled={linhas.length === 0}
          >
            📄 PDF
          </button>
          <button
            className={styles.btnExp}
            onClick={handleExcel}
            disabled={linhas.length === 0}
          >
            📊 Excel
          </button>
        </div>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {linhas.length} {linhas.length === 1 ? 'posição' : 'posições'} para repor
          </span>
        </div>
        <div className={styles.tabelaScroll}>
          <table className={styles.tabela}>
            <thead>
              <tr>
                <th>Cor</th>
                <th>Resina</th>
                <th>Estoque</th>
                <th className={styles.textRight}>Qtd (L)</th>
                <th className={styles.textRight}>Mín</th>
                <th className={styles.textRight}>Repor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Carregando...
                  </td>
                </tr>
              ) : linhas.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.semResultados}>
                    Nenhuma posição abaixo do mínimo.
                  </td>
                </tr>
              ) : (
                linhas.map((l, idx) => (
                  <tr key={idx}>
                    <td><strong>{l.cor || '—'}</strong></td>
                    <td>{l.resina || '—'}</td>
                    <td>{l.estoque}</td>
                    <td className={`${styles.textRight} ${styles.warn}`}>
                      <strong>{l.qtd}</strong>
                    </td>
                    <td className={`${styles.textRight} ${styles.muted}`}>
                      {l.minimo}
                    </td>
                    <td className={`${styles.textRight} ${styles.warn}`}>
                      <strong>+{l.repor}</strong>
                    </td>
                    <td>
                      <span
                        className={`${styles.badge} ${
                          l.statusKey === STATUS.ATENCAO
                            ? styles.badgeAtencao
                            : styles.badgeWarn
                        }`}
                      >
                        {l.statusLabel}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
